/**
 * Step 3 — Gibberish & nonsense detection.
 * Excessive repeated chars, no-vowel strings, keyboard smash, ALL CAPS, max word length.
 */

import { normalizeText } from './normalizeText.js';

/**
 * @param {string} text
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig['gibberish']} gConfig
 * @param {import('./contentSafetyConfig.js').defaultContentSafetyConfig['messages']} messages
 * @returns {{ valid: boolean, errorCode?: string, message?: string }}
 */
export function checkGibberish(text, gConfig, messages) {
  const normalized = normalizeText(text || '');
  if (normalized.length < 4) return { valid: true };

  const maxRepeated = gConfig?.maxRepeatedChars ?? 3;
  const maxCapsRatio = gConfig?.maxCapsRatio ?? 0.8;
  const maxWordLength = gConfig?.maxWordLength ?? 50;
  const noVowelMinLength = gConfig?.noVowelMinLength ?? 4;
  const keyboardSmash = gConfig?.keyboardSmashPatterns ?? [];

  // Repeated character run (e.g. "aaaaaaa")
  const repeatedCharRe = new RegExp(`(.)\\1{${maxRepeated},}`, 'i');
  if (repeatedCharRe.test(normalized)) {
    return { valid: false, errorCode: 'GIBBERISH', message: messages?.gibberish ?? 'Please use clear, readable text.' };
  }

  // ALL CAPS
  const letters = normalized.replace(/\s/g, '');
  if (letters.length >= 5) {
    const caps = (normalized.match(/[A-Z]/g) || []).length;
    if (caps / letters.length > maxCapsRatio) {
      return { valid: false, errorCode: 'ALL_CAPS', message: messages?.allCaps ?? 'Please avoid typing in all capital letters.' };
    }
  }

  // Single word too long
  const words = normalized.split(/\s+/);
  const vowelRe = /[aeiouyAEIOUY]/;
  for (const w of words) {
    if (w.length > maxWordLength) {
      return { valid: false, errorCode: 'GIBBERISH', message: messages?.gibberish ?? 'Please use clear, readable text.' };
    }
    // No-vowel strings (spec: where language requires vowels)
    if (w.length >= noVowelMinLength && !vowelRe.test(w)) {
      return { valid: false, errorCode: 'NO_VOWEL', message: messages?.noVowel ?? 'Please use real words; avoid strings with no vowels.' };
    }
  }

  // Keyboard smash (exact substring)
  const lower = normalized.toLowerCase();
  for (const pattern of keyboardSmash) {
    if (lower.includes(pattern) && normalized.length < 20) {
      return { valid: false, errorCode: 'GIBBERISH', message: messages?.gibberish ?? 'Please use clear, readable text.' };
    }
  }

  return { valid: true };
}
